/**
 * Découpe une liste d'athlètes en N listes de tailles équilibrées (max 1 d'écart)
 */
export const splitBalanced = (athletes, nbPoules) => {
  const n = parseInt(nbPoules) || 1;
  if (n <= 1) return [athletes];

  const result = [];
  const total = athletes.length;
  const minSize = Math.floor(total / n);
  const extra = total % n;

  let currentIdx = 0;
  for (let i = 0; i < n; i++) {
    const size = i < extra ? minSize + 1 : minSize;
    result.push(athletes.slice(currentIdx, currentIdx + size));
    currentIdx += size;
  }
  return result;
};

const shuffleArray = (array) => {
  const newArr = [...array];
  for (let i = newArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
  }
  return newArr;
};

/**
 * GÉNÉRATEUR UNIVERSEL DE BRACKET (Élimination directe 2 à 12+)
 * Retourne un tableau de rounds [[matchs tour 1], [matchs tour 2]...]
 */
function generateBracketRounds(athletes) {
  const nb = athletes.length;
  if (nb < 2) return [];

  let size = 2;
  while (size < nb) size *= 2;

  let rounds = [];
  
  // 1. Premier Tour (avec BYES)
  const firstRoundMatches = [];
  // On nomme les étapes de façon normalisée pour le tri futur
  const firstRoundLabel = size === 16 ? "1/8 Finale" : size === 8 ? "Quart de finale" : size === 4 ? "Demi-finale" : "Finale";
  
  for (let i = 0; i < size / 2; i++) {
    const a1 = athletes[i];
    const a2 = athletes[size - 1 - i]; 

    if (a1 && a2) {
      firstRoundMatches.push({
        id: crypto.randomUUID(),
        athlete1_id: a1.id,
        athlete2_id: a2.id,
        label: firstRoundLabel,
        winner_id: null
      });
    } else if (a1 || a2) {
      const winner = a1 || a2;
      firstRoundMatches.push({
        id: crypto.randomUUID(),
        athlete1_id: winner.id,
        athlete2_id: null,
        label: firstRoundLabel,
        winner_id: winner.id,
        is_bye: true 
      });
    }
  }
  rounds.push(firstRoundMatches.filter(m => !m.is_bye));

  // 2. Tours suivants
  let currentMatches = firstRoundMatches;
  let nextSize = size / 2;

  while (nextSize > 1) {
    const nextRoundMatches = [];
    const currentRoundLabel = nextSize === 16 ? "1/8 Finale" : nextSize === 8 ? "Quart de finale" : nextSize === 4 ? "Demi-finale" : "Finale";
    const nextRoundSize = nextSize / 2;
    const nextRoundLabel = nextRoundSize === 8 ? "Quart de finale" : nextRoundSize === 4 ? "Demi-finale" : nextRoundSize === 2 ? "Demi-finale" : "Finale";
    
    // Correction du label pour le round suivant (le label du match qu'on est en train de CREER)
    const labelMatchAAjouter = nextSize === 8 ? "Quart de finale" : nextSize === 4 ? "Demi-finale" : "Finale";

    for (let i = 0; i < nextSize / 2; i++) {
      const m1 = currentMatches[i * 2];
      const m2 = currentMatches[i * 2 + 1];

      nextRoundMatches.push({
        id: crypto.randomUUID(),
        athlete1_id: m1.winner_id || null,
        athlete2_id: m2.winner_id || null,
        source_match_id: m1.id,
        source_match_id2: m2.id,
        label: labelMatchAAjouter,
        placeholder1: m1.winner_id ? null : `Vainqueur ${m1.label}`,
        placeholder2: m2.winner_id ? null : `Vainqueur ${m2.label}`
      });
    }
    rounds.push(nextRoundMatches);
    currentMatches = nextRoundMatches;
    nextSize /= 2;
  }

  return rounds;
}

/**
 * Algorithme de Berger (round-robin) pour N joueurs.
 * Retourne un tableau de rounds, chaque round étant un tableau de paires {a, b} (indices).
 * - N pair  → N-1 rounds de N/2 matchs
 * - N impair → N rounds de (N-1)/2 matchs (le slot bye est ignoré)
 */
const generatePouleRounds = (athletes) => {
  const n = athletes.length;
  if (n < 2) return [];

  const effective = n % 2 === 0 ? n : n + 1; // joueur "fictif" si impair
  const numRounds = effective - 1;
  const rounds = [];

  for (let round = 0; round < numRounds; round++) {
    const roundMatches = [];
    // Position 0 = joueur fixe (effective-1). Positions 1..effective-1 tournent.
    const positions = [effective - 1];
    for (let i = 0; i < effective - 1; i++) {
      positions.push((round + i) % (effective - 1));
    }
    // On apparie position i avec position (effective-1-i)
    for (let i = 0; i < effective / 2; i++) {
      const p1 = positions[i];
      const p2 = positions[effective - 1 - i];
      if (p1 < n && p2 < n) {
        roundMatches.push({ a: p1, b: p2 });
      }
    }
    if (roundMatches.length > 0) rounds.push(roundMatches);
  }
  return rounds;
};

/**
 * Répartit les athlètes en N poules en plaçant les têtes de série aux extrémités.
 * Seed 1 → poule 0 (A), Seed 2 → poule N-1 (dernière).
 * Les poules les plus petites sont placées aux extrémités pour les têtes de série.
 */
const generateSeededPools = (athletes, nbPools) => {
  const seed1 = athletes.find(a => a.seed === 1);
  const seed2 = athletes.find(a => a.seed === 2);
  const others = shuffleArray(athletes.filter(a => a.seed !== 1 && a.seed !== 2));

  const total = athletes.length;
  const minSize = Math.floor(total / nbPools);
  const extra = total % nbPools;

  // Tailles triées par ordre croissant
  const sortedSizes = Array.from({ length: nbPools }, (_, i) =>
    i < extra ? minSize + 1 : minSize
  ).sort((a, b) => a - b);

  // Réarrangement : plus petites tailles aux positions 0 et N-1
  const poolSizes = new Array(nbPools);
  poolSizes[0] = sortedSizes[0];
  if (nbPools > 1) poolSizes[nbPools - 1] = sortedSizes[1];
  for (let i = 1; i < nbPools - 1; i++) poolSizes[i] = sortedSizes[i + 1];

  // Construction des poules
  const pools = Array.from({ length: nbPools }, () => []);
  if (seed1) pools[0].push(seed1);
  if (seed2) pools[nbPools > 1 ? nbPools - 1 : 0].push(seed2);

  let othersIdx = 0;
  for (let i = 0; i < nbPools; i++) {
    while (pools[i].length < poolSizes[i] && othersIdx < others.length) {
      pools[i].push(others[othersIdx++]);
    }
  }

  return pools;
};

export const generateInitialPoules = (categories, formatsConfig, nbRings) => {
  const poulesStructure = {};
  const matchCountsPerRing = {};
  const sameRingPerCategory = formatsConfig.__sameRingPerCategory !== false;
  const useSeeding = formatsConfig.__useSeeding === true;

  for (let i = 1; i <= nbRings; i++) {
    poulesStructure[`ring${i}`] = [];
    matchCountsPerRing[i] = 0;
  }

  const randomizedCats = shuffleArray(categories);

  randomizedCats.forEach((cat) => {
    const athletes = cat.athletes || [];
    if (athletes.length < 2) return;

    let rule = formatsConfig[athletes.length.toString()];

    if (!rule || (rule.format === 'poule_unique' && athletes.length >= 6)) {
      const count = athletes.length;
      if (count === 2) rule = { format: "finale_directe", nb: 1 };
      else if (count >= 3 && count <= 5) rule = { format: "poule_unique", nb: 1 };
      else if (count >= 6 && count <= 8) rule = { format: "poules", nb: 2 };
      else if (count >= 9 && count <= 11) rule = { format: "poules", nb: 3 };
      else if (count === 12) rule = { format: "poules", nb: 3 };
      else if (count >= 13 && count <= 15) rule = { format: "poules", nb: 4 };
      else rule = { format: "poule_unique", nb: 1 };
    }

    if (athletes.length === 2) {
      rule = { format: "finale_directe", nb: 1 };
    }

    const isElim = rule.format === 'elimination' || rule.format === 'elimination_directe';
    const nbToSplit = (rule.format === 'poules' || rule.format === 'poule_unique') ? (parseInt(rule.nb) || 1) : 1;
    const hasSeeds = athletes.some(a => a.seed === 1 || a.seed === 2);
    const poulesList = (useSeeding && !isElim && nbToSplit > 1 && hasSeeds)
      ? generateSeededPools(athletes, nbToSplit)
      : splitBalanced(athletes, nbToSplit);

    const findBestRing = () => {
      let best = 1;
      let min = matchCountsPerRing[1];
      for (let r = 1; r <= nbRings; r++) {
        if (matchCountsPerRing[r] < min) {
          min = matchCountsPerRing[r];
          best = r;
        }
      }
      return best;
    };

    const buildPool = (athletesList, i) => {
      const n = athletesList.length;
      let label = "Poule Unique";
      if (poulesList.length > 1) label = `Poule ${String.fromCharCode(65 + i)} (${n})`;
      else if (isElim) label = "Élimination Directe";
      else if (rule.format === "finale_directe") label = "FINALE";
      return {
        id: `poule_${cat.id}_${i}`,
        category_id: cat.id,
        category_name: cat.name,
        poule_label: label,
        format: rule.format,
        type: (isElim || rule.format === "finale_directe") ? 'elimination' : 'poule',
        athletes: athletesList
      };
    };

    if (sameRingPerCategory || isElim) {
      // Toutes les poules de la catégorie sur le même ring (ou bracket indivisible)
      let totalMatchesInCat = 0;
      if (isElim) {
        let size = 2;
        while (size < athletes.length) size *= 2;
        totalMatchesInCat = size - 1;
      } else {
        poulesList.forEach(al => { totalMatchesInCat += (al.length * (al.length - 1)) / 2; });
      }
      const bestRing = findBestRing();
      const ringKey = `ring${bestRing}`;
      poulesList.forEach((athletesList, i) => {
        poulesStructure[ringKey].push(buildPool(athletesList, i));
      });
      matchCountsPerRing[bestRing] += totalMatchesInCat;
    } else {
      // Chaque poule est affectée indépendamment au ring le moins chargé
      poulesList.forEach((athletesList, i) => {
        const bestRing = findBestRing();
        const ringKey = `ring${bestRing}`;
        poulesStructure[ringKey].push(buildPool(athletesList, i));
        matchCountsPerRing[bestRing] += (athletesList.length * (athletesList.length - 1)) / 2;
      });
    }
  });

  return poulesStructure;
};

// ... (garder splitBalanced, shuffleArray, generateBracketRounds et generatePouleRounds identiques) ...

/**
 * Génère l'ordre final des matchs à partir du plateau.
 *
 * Principe :
 * 1. Séparer les "finales directes" (2 athlètes, 1 match) des groupes réguliers.
 * 2. Pour chaque groupe régulier, générer ses tours (Berger ou bracket).
 * 3. Trier les groupes par nombre de matchs au 1er tour (desc) — ordre fixe pour tous les tours.
 * 4. Construire les vagues : vague[r] = tous les matchs du tour r de chaque groupe, dans l'ordre trié.
 * 5. Intercaler les finales directes entre les vagues (1 par gap) pour la récupération.
 * 6. Numéroter et résoudre les placeholders.
 */
export const generateFinalOrder = (plateau) => {
  const sortedRings = Object.keys(plateau).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  const matchesByRing = {};

  sortedRings.forEach(ringKey => {
    const ringId = parseInt(ringKey.replace("ring", ""));
    const groupsInRing = plateau[ringKey];

    // 1. Séparer finales directes (2 athlètes) des groupes à tours multiples
    const directFinaleGroups = groupsInRing.filter(
      g => g.format === 'finale_directe' && (g.athletes?.length || 0) >= 2
    );
    const regularGroups = groupsInRing.filter(
      g => g.format !== 'finale_directe' && (g.athletes?.length || 0) >= 2
    );

    // 2. Générer les tours de chaque groupe régulier
    const groupRounds = {};
    regularGroups.forEach(group => {
      const athletes = group.athletes || [];
      if (athletes.length < 2) return;

      const isElim = group.type === 'elimination'
        || group.format === 'elimination'
        || group.format === 'elimination_directe';

      let rawRounds;
      if (isElim) {
        rawRounds = generateBracketRounds(athletes);
      } else {
        rawRounds = generatePouleRounds(athletes).map(r =>
          r.map(m => ({
            id: crypto.randomUUID(),
            athlete1_id: athletes[m.a].id,
            athlete2_id: athletes[m.b].id,
            label: group.poule_label,
          }))
        );
      }

      // Enrichir chaque match avec les infos du groupe
      groupRounds[group.id] = rawRounds.map(round =>
        round.map(m => ({
          ...m,
          ring_id: ringId,
          category_id: group.category_id,
          category_name: group.category_name,
          poule_id: group.id,
          poule_label: m.label || group.poule_label,
          source_match_id: m.source_match_id || null,
          source_match_id2: m.source_match_id2 || null,
          placeholder1: m.placeholder1 || null,
          placeholder2: m.placeholder2 || null,
          type: isElim ? 'elimination' : 'poule',
        }))
      );
    });

    // 3. Trier les groupes par taille du 1er tour (desc) — ordre fixe pour tous les tours
    const sortedGroups = [...regularGroups].sort((a, b) => {
      const countA = groupRounds[a.id]?.[0]?.length || 0;
      const countB = groupRounds[b.id]?.[0]?.length || 0;
      return countB - countA;
    });

    // 4. Construire les vagues tour par tour, dans l'ordre trié
    const maxRounds = sortedGroups.reduce(
      (max, g) => Math.max(max, groupRounds[g.id]?.length || 0),
      0
    );

    const waves = [];
    for (let r = 0; r < maxRounds; r++) {
      const waveMatches = [];
      sortedGroups.forEach(group => {
        const round = groupRounds[group.id]?.[r];
        if (round && round.length > 0) waveMatches.push(...round);
      });
      if (waveMatches.length > 0) waves.push(waveMatches);
    }

    // 5. Générer les matchs de finales directes
    const directFinaleMatchesList = directFinaleGroups.map(group => ({
      id: crypto.randomUUID(),
      athlete1_id: group.athletes[0].id,
      athlete2_id: group.athletes[1].id,
      ring_id: ringId,
      category_id: group.category_id,
      category_name: group.category_name,
      poule_id: group.id,
      poule_label: group.poule_label,
      label: 'Finale',
      type: 'elimination',
      source_match_id: null,
      source_match_id2: null,
      placeholder1: null,
      placeholder2: null,
    }));

    // 6. Intercaler : distribuer toutes les finales directes équitablement entre les vagues
    const finalList = [];
    const numGaps = Math.max(waves.length - 1, 0);
    const numFinales = directFinaleMatchesList.length;

    // Calculer combien de finales mettre dans chaque gap (répartition la plus équitable possible)
    // Les premiers gaps reçoivent 1 de plus si la division n'est pas entière
    const finalesPerGap = numGaps > 0
      ? Array.from({ length: numGaps }, (_, i) =>
          Math.floor(numFinales / numGaps) + (i < numFinales % numGaps ? 1 : 0)
        )
      : [];

    let finaleIdx = 0;
    for (let w = 0; w < waves.length; w++) {
      finalList.push(...waves[w]);
      if (w < numGaps) {
        const count = finalesPerGap[w];
        for (let f = 0; f < count; f++) {
          finalList.push(directFinaleMatchesList[finaleIdx++]);
        }
      }
    }
    // Finales restantes si pas de vagues du tout
    while (finaleIdx < numFinales) {
      finalList.push(directFinaleMatchesList[finaleIdx++]);
    }

    // 7. Numérotation
    const numberedMatches = finalList.map((m, idx) => ({ ...m, match_ref: (idx + 1).toString() }));

    // 8. Résolution des placeholders pour les brackets
    const finalMatches = numberedMatches.map(match => {
      let p1 = match.placeholder1;
      let p2 = match.placeholder2;
      if (match.source_match_id) {
        const s1 = numberedMatches.find(m => String(m.id) === String(match.source_match_id));
        if (s1) p1 = `Vainqueur V${s1.match_ref}`;
      }
      if (match.source_match_id2) {
        const s2 = numberedMatches.find(m => String(m.id) === String(match.source_match_id2));
        if (s2) p2 = `Vainqueur V${s2.match_ref}`;
      }
      return { ...match, placeholder1: p1, placeholder2: p2 };
    });

    matchesByRing[ringId] = finalMatches;
  });

  return Object.values(matchesByRing).flat();
};
